import React, { useState, useRef, useEffect, forwardRef, useMemo } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  ListFilter,
  Tag,
  UserCircle,
  Check,
  ChevronLeft,
  CircleUserRound,
  Clock,
  Minus,
  ClockAlert,
  CircleDashed,
  TriangleDashed,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { HalfFilledTriangleIcon } from "@/assets/icons/half-filled-triangle-icon";
import { NoPriorityIcon, defaultPriorities } from "@/types/priorities";
import { ESTIMATE_POINTS, getTShirtSize, Issue } from "@/types/issues";
import { ShortcutHandler } from "../providers/ShortcutProvider";
import { useProjectWithUsers } from "@/hooks/useProjectWithUsers";
import { useCanvasStatusManagement } from "@/components/ProjectConfiguration/hooks";
import { STATE_KEY_TO_STATUS_ID } from "@/components/ProjectConfiguration/constants";
import { columnStatuses } from "./CanvasKanban";
import { UserAvatar } from "../common/UserAvatar";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { User } from "@/types/users";
import { getColorValue } from "@/utils/labelUtils";
import { Status } from "@/types/status";
import { KanbanDefaultStates } from "./CanvasKanban";
import { FilterState } from "./types";
import { getDefaultFilters } from "@/utils/filterUrlUtils";

const STATUS_ID_TO_STATE_KEY = Object.fromEntries(
  Object.entries(STATE_KEY_TO_STATUS_ID).map(([key, value]) => [value, key]),
);

const FILTER_VALUES = {
  NONE: "none",
  OVERDUE: "overdue",
  TODAY: "today",
  THIS_WEEK: "thisWeek",
  NEXT_WEEK: "nextWeek",
  LATER: "later",
} as const;

interface FilterOption {
  id: string;
  label: string;
  icon: React.ElementType;
  props?: any;
}
import { DueDateFilterValue } from "@/types/filterEnums";
import {
  filterOptions,
  dueDateFilterOptions,
  FilterCount,
  calculateFilterCounts,
  createSelectedFiltersSet,
  updateFiltersFromSelected,
  filterMenuItems,
  scrollbarStyles,
} from "@/utils/filterUtils";

interface FilterMenuProps {
  className?: string;
  onOpenChange?: (open: boolean) => void;
  filters?: FilterState;
  updateFilters: (filters: Partial<FilterState>) => void;
}

interface FilterMenuHandle {
  clearFilters: () => void;
  openMenu: () => void;
}

export const FilterMenu = forwardRef<FilterMenuHandle, FilterMenuProps>(
  ({ className, onOpenChange, filters, updateFilters }, ref) => {
    const project = useProjectWithUsers();

    const labels = project?.labels || [];

    // Get comprehensive status list using our reusable hook
    const { comprehensiveStatusList } = useCanvasStatusManagement();
    const statuses = comprehensiveStatusList;

    const users = useMemo(() => {
      const defaultUsersList = Array.isArray(project?.users)
        ? project.users
        : [];

      const sortedUsersList = [...defaultUsersList].sort((a: User, b: User) => {
        const aName = a.name?.toLowerCase() || a.email?.toLowerCase();
        const bName = b.name?.toLowerCase() || b.email?.toLowerCase();
        return aName.localeCompare(bName);
      });
      return sortedUsersList;
    }, [project]);

    const allIssues = useIssuesStore((state) =>
      Object.values(state.issuesByProject).flatMap((projectIssues) =>
        Object.values(projectIssues),
      ),
    );

    const filterCounts = useMemo(() => {
      if (!project?.id) return null;

      const availableStatusNames = statuses.map((status) => status.name);

      return calculateFilterCounts(allIssues, project.id, availableStatusNames);
    }, [allIssues, project?.id, statuses]);

    const assigneesWithIssues = useMemo(() => users, [users]);

    const unassignedCount = useMemo(
      () => filterCounts?.assignees[DueDateFilterValue.NONE] || 0,
      [filterCounts?.assignees],
    );

    const labelsWithIssues = useMemo(
      () =>
        labels
          .filter((label) => {
            return label && label.label && label.label.length > 0;
          })
          .sort((a, b) => {
            const labelA = a?.label?.toLowerCase() || "";
            const labelB = b?.label?.toLowerCase() || "";

            return labelA.localeCompare(labelB);
          }),
      [labels],
    );

    const noLabelCount = useMemo(
      () => filterCounts?.labels[DueDateFilterValue.NONE] || 0,
      [filterCounts?.labels],
    );

    // Convert filter state to selectedFilters Set for the UI
    const selectedFilters = useMemo(() => {
      if (!filters) return new Set<string>();

      return createSelectedFiltersSet(
        filters.statuses || [],
        filters.labels || [],
        filters.userIds || [],
        filters.estimates || [],
        filters.priorities || [],
        filters.dueDates || {
          none: false,
          overdue: false,
          today: false,
          thisWeek: false,
          nextWeek: false,
          later: false,
        },
      );
    }, [filters]);

    const [searchQuery, setSearchQuery] = useState("");
    const [activeFilter, setActiveFilter] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [selectedIndex, setSelectedIndex] = useState(-1);

    // Expose methods through ref
    React.useImperativeHandle(ref, () => ({
      clearFilters: () => {
        updateFilters(getDefaultFilters());
      },
      openMenu: () => {
        setIsOpen(true);
      },
    }));

    // Helper function to update filters based on selectedFilters Set
    const handleUpdateFiltersFromSelected = (
      newSelectedFilters: Set<string>,
    ) => {
      if (!project) {
        return;
      }
      updateFiltersFromSelected(
        newSelectedFilters,
        (projectId, newFilters) => {
          updateFilters(newFilters);
        },
        project.id,
      );
    };

    useEffect(() => {
      if (isOpen) {
        // Use a small timeout to ensure the input is focused after the dropdown renders
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      }
    }, [isOpen, activeFilter]);

    // Add effect to maintain focus
    useEffect(() => {
      const input = inputRef.current;
      const menu = menuRef.current;
      if (!input || !isOpen) return;

      const handleFocusOut = (e: FocusEvent) => {
        // Prevent the blur and immediately restore focus
        e.preventDefault();
        if (isOpen) {
          // Use requestAnimationFrame to ensure focus is restored after any React updates
          requestAnimationFrame(() => {
            input.focus();
          });
        }
      };

      const handleMouseDown = (e: MouseEvent) => {
        // Don't prevent default on menu items or the input itself
        const target = e.target as HTMLElement;
        if (!target.closest('[role="menuitem"]') && target !== input) {
          e.preventDefault();
        }
        // Ensure input maintains focus even during mousedown
        requestAnimationFrame(() => {
          if (isOpen) {
            input.focus();
          }
        });
      };

      // Apply the mousedown handler to the entire dropdown content
      menu?.addEventListener("mousedown", handleMouseDown, { capture: true });
      input.addEventListener("blur", handleFocusOut);

      // Ensure focus is maintained after filtering
      const maintainFocus = () => {
        if (isOpen) {
          input.focus();
        }
      };

      // Add a small delay to ensure focus after React updates
      const timeoutId = setTimeout(maintainFocus, 0);

      return () => {
        clearTimeout(timeoutId);
        menu?.removeEventListener("mousedown", handleMouseDown, {
          capture: true,
        });
        input.removeEventListener("blur", handleFocusOut);
      };
    }, [isOpen, activeFilter, searchQuery]); // Add searchQuery as dependency

    // Separate effect for handling search query changes
    useEffect(() => {
      if (isOpen && inputRef.current) {
        // Ensure input maintains focus after search query updates
        requestAnimationFrame(() => {
          inputRef.current?.focus();
        });
      }
    }, [searchQuery, isOpen]);

    useEffect(() => {
      if (!isOpen) return;

      const handleMouseMove = (e: MouseEvent) => {
        // Remove auto-focus behavior on mouse move
        if (menuRef.current?.contains(e.target as Node) && activeFilter) {
          return;
        }
      };

      document.addEventListener("mousemove", handleMouseMove);
      return () => document.removeEventListener("mousemove", handleMouseMove);
    }, [isOpen, activeFilter]);

    useEffect(() => {
      // Reset selection when filter changes
      setSelectedIndex(-1);
    }, [searchQuery, activeFilter]);

    const handleKeyNavigation = (e: React.KeyboardEvent) => {
      const menuItems = menuRef.current?.querySelectorAll('[role="menuitem"]');
      if (!menuItems?.length) return;

      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();

        const direction = e.key === "ArrowDown" ? 1 : -1;
        const newIndex =
          selectedIndex === -1
            ? direction === 1
              ? 0
              : menuItems.length - 1
            : (selectedIndex + direction + menuItems.length) % menuItems.length;

        // Remove data-highlighted from all items
        menuItems.forEach((item) => {
          item.removeAttribute("data-highlighted");
        });

        // Add data-highlighted to the selected item
        menuItems[newIndex].setAttribute("data-highlighted", "true");

        setSelectedIndex(newIndex);
      } else if (e.key === "Enter" && selectedIndex !== -1) {
        e.preventDefault();
        (menuItems[selectedIndex] as HTMLElement).click();
      }
    };

    // Reset highlighted state when menu closes or filter changes
    useEffect(() => {
      if (!isOpen) {
        const menuItems =
          menuRef.current?.querySelectorAll('[role="menuitem"]');
        menuItems?.forEach((item) => {
          item.removeAttribute("data-highlighted");
        });
        setSelectedIndex(-1);
      }
    }, [isOpen, searchQuery, activeFilter]);

    const handleOpenChange = (open: boolean) => {
      // If we're in a submenu and trying to close, only allow it from the back button
      if (
        !open &&
        activeFilter &&
        !menuRef.current?.contains(document.activeElement)
      ) {
        return;
      }
      setIsOpen(open);
      if (!open) {
        setSearchQuery("");
        setActiveFilter(null);
      }
      onOpenChange?.(open);
    };

    const renderFilterContent = () => {
      if (!activeFilter) {
        const filteredOptions = filterOptions.filter((option) =>
          searchQuery
            ? option.label?.toLowerCase().includes(searchQuery.toLowerCase())
            : true,
        );

        return (
          <div className="flex flex-col gap-1 px-1 body text-xs font-normal">
            <DropdownMenuItem
              className={cn(
                "flex items-center gap-2 cursor-pointer hover:bg-secondary-n-800 data-highlighted:bg-secondary-n-800",
                selectedFilters.size === 0 &&
                  "opacity-50 cursor-not-allowed pointer-events-none",
              )}
              onClick={(e) => {
                e.preventDefault();
                handleUpdateFiltersFromSelected(new Set());
              }}
              onSelect={(e) => e.preventDefault()}
            >
              <span className="flex-1 text-sm text-muted-foreground">
                Clear all filters
              </span>
            </DropdownMenuItem>
            {filteredOptions.map((option) => {
              const hasActiveFilters = Array.from(selectedFilters).some(
                (filter) => filter.startsWith(`${option.id}-`),
              );

              return (
                <DropdownMenuItem
                  key={option.id}
                  className="flex items-center gap-2 cursor-pointer p-1 hover:bg-secondary-n-800 data-highlighted:bg-secondary-n-800"
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveFilter(option.id);
                    setSearchQuery("");
                  }}
                  onSelect={(e) => e.preventDefault()}
                >
                  <div className="size-5 flex items-center justify-center">
                    <option.icon
                      className={cn("size-4 text-muted-foreground")}
                      {...option.props}
                    />
                  </div>
                  <span className="flex-1">{option.label}</span>
                  {hasActiveFilters && (
                    <div className="size-5 flex items-center justify-center">
                      <Check />
                    </div>
                  )}
                </DropdownMenuItem>
              );
            })}
          </div>
        );
      }

      const option = filterOptions.find((opt) => opt.id === activeFilter);

      const getMenuItems = () => {
        let items: JSX.Element[] = [];

        if (option?.id === "status") {
          items = statuses.map((status) => {
            const isDefaultStatus = Object.values(
              STATE_KEY_TO_STATUS_ID,
            ).includes(status.id as any);
            const filterValue = isDefaultStatus ? status.id : status.name;

            return (
              <DropdownMenuCheckboxItem
                key={status.id}
                className="flex items-center gap-2 select-none hover:bg-secondary-n-800 data-highlighted:bg-secondary-n-800"
                checked={selectedFilters.has(`status-${filterValue}`)}
                onCheckedChange={(checked) => {
                  const newSelectedFilters = new Set(selectedFilters);
                  if (checked) {
                    newSelectedFilters.add(`status-${filterValue}`);
                  } else {
                    newSelectedFilters.delete(`status-${filterValue}`);
                  }
                  handleUpdateFiltersFromSelected(newSelectedFilters);
                }}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className="size-5 flex items-center justify-center shrink-0">
                    {status.filterIcon ? (
                      <status.filterIcon />
                    ) : (
                      <status.icon />
                    )}
                  </div>
                  <span className="truncate max-w-[120px]" title={status.name}>
                    {status.name}
                  </span>
                  {filterCounts && (
                    <FilterCount
                      count={filterCounts.statuses[status.name] || 0}
                    />
                  )}
                </div>
              </DropdownMenuCheckboxItem>
            );
          });
        } else if (option?.id === "assignee") {
          const showUnassigned = unassignedCount > 0;

          items = [
            ...(showUnassigned
              ? [
                  <DropdownMenuCheckboxItem
                    key="none"
                    className="flex items-center gap-2 select-none"
                    checked={selectedFilters.has("assignee-none")}
                    onCheckedChange={(checked) => {
                      const newSelectedFilters = new Set(selectedFilters);
                      if (checked) {
                        newSelectedFilters.add("assignee-none");
                      } else {
                        newSelectedFilters.delete("assignee-none");
                      }
                      handleUpdateFiltersFromSelected(newSelectedFilters);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <CircleUserRound className="size-5 text-secondary-n-600" />
                      <span>Unassigned</span>
                    </div>
                    <FilterCount count={unassignedCount} />
                  </DropdownMenuCheckboxItem>,
                ]
              : []),
            ...assigneesWithIssues.map((user) => (
              <DropdownMenuCheckboxItem
                key={user.id}
                className="flex items-center gap-2 select-none hover:bg-secondary-n-800 data-highlighted:bg-secondary-n-800"
                checked={selectedFilters.has(`assignee-${user.id}`)}
                onCheckedChange={(checked) => {
                  const newSelectedFilters = new Set(selectedFilters);
                  if (checked) {
                    newSelectedFilters.add(`assignee-${user.id}`);
                  } else {
                    newSelectedFilters.delete(`assignee-${user.id}`);
                  }
                  handleUpdateFiltersFromSelected(newSelectedFilters);
                }}
              >
                <div className="flex items-center gap-2">
                  <UserAvatar
                    userName={user.name || user.email}
                    userId={user.id}
                  />
                  <span className="truncate max-w-[110px]">
                    {user.name || user.email}
                  </span>
                </div>
                <FilterCount count={filterCounts?.assignees[user.id] || 0} />
              </DropdownMenuCheckboxItem>
            )),
          ];
        } else if (option?.id === "priority") {
          items = [
            <DropdownMenuCheckboxItem
              key="none"
              className="flex items-center gap-2 select-none hover:bg-secondary-n-800 data-highlighted:bg-secondary-n-800"
              checked={selectedFilters.has("priority-0")}
              onCheckedChange={(checked) => {
                const newSelectedFilters = new Set(selectedFilters);
                if (checked) {
                  newSelectedFilters.add("priority-0");
                } else {
                  newSelectedFilters.delete("priority-0");
                }
                handleUpdateFiltersFromSelected(newSelectedFilters);
              }}
            >
              <div className="flex items-center gap-2">
                <Minus className="size-4 text-muted-foreground" />
                <span>No Priority</span>
              </div>
              {filterCounts && (
                <FilterCount count={filterCounts.priorities["0"] || 0} />
              )}
            </DropdownMenuCheckboxItem>,
            ...defaultPriorities.map((priority) => (
              <DropdownMenuCheckboxItem
                key={priority.id}
                className="flex items-center gap-2 select-none hover:bg-secondary-n-800 data-highlighted:bg-secondary-n-800"
                checked={selectedFilters.has(`priority-${priority.id}`)}
                onCheckedChange={(checked) => {
                  const newSelectedFilters = new Set(selectedFilters);
                  if (checked) {
                    newSelectedFilters.add(`priority-${priority.id}`);
                  } else {
                    newSelectedFilters.delete(`priority-${priority.id}`);
                  }
                  handleUpdateFiltersFromSelected(newSelectedFilters);
                }}
              >
                <div className="flex items-center gap-2">
                  <priority.icon className="size-4 text-muted-foreground" />
                  <span>{priority.name}</span>
                </div>
                {filterCounts && (
                  <FilterCount
                    count={filterCounts.priorities[priority.id.toString()] || 0}
                  />
                )}
              </DropdownMenuCheckboxItem>
            )),
          ];
        } else if (option?.id === "label") {
          const showNoLabel = noLabelCount > 0;

          items = [
            ...(showNoLabel
              ? [
                  <DropdownMenuCheckboxItem
                    key="none"
                    className="flex items-center gap-2 select-none hover:bg-secondary-n-800 data-highlighted:bg-secondary-n-800"
                    checked={selectedFilters.has("label-none")}
                    onCheckedChange={(checked) => {
                      const newSelectedFilters = new Set(selectedFilters);
                      if (checked) {
                        newSelectedFilters.add("label-none");
                      } else {
                        newSelectedFilters.delete("label-none");
                      }
                      handleUpdateFiltersFromSelected(newSelectedFilters);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Tag className="size-4 text-muted-foreground" />
                      <span>No label</span>
                    </div>
                    <FilterCount count={noLabelCount} />
                  </DropdownMenuCheckboxItem>,
                ]
              : []),
            ...labelsWithIssues.map((label) => (
              <DropdownMenuCheckboxItem
                key={label.id}
                className="flex items-center gap-2 select-none hover:bg-secondary-n-800 data-highlighted:bg-secondary-n-800"
                checked={selectedFilters.has(`label-${label.id}`)}
                onCheckedChange={(checked) => {
                  const newSelectedFilters = new Set(selectedFilters);
                  if (checked) {
                    newSelectedFilters.add(`label-${label.id}`);
                  } else {
                    newSelectedFilters.delete(`label-${label.id}`);
                  }
                  handleUpdateFiltersFromSelected(newSelectedFilters);
                }}
              >
                <div className="flex items-center gap-2">
                  <div
                    className="size-2.5 rounded-[3px] shrink-0"
                    style={{
                      backgroundColor: getColorValue(label.color),
                    }}
                  />
                  <span>{label.label}</span>
                </div>
                <FilterCount count={filterCounts?.labels[label.id] || 0} />
              </DropdownMenuCheckboxItem>
            )),
          ];
        } else if (option?.id === "estimate") {
          items = [
            <DropdownMenuCheckboxItem
              key="none"
              className="flex items-center gap-2 select-none hover:bg-secondary-n-800 data-highlighted:bg-secondary-n-800"
              checked={selectedFilters.has("estimate-none")}
              onCheckedChange={(checked) => {
                const newSelectedFilters = new Set(selectedFilters);
                if (checked) {
                  newSelectedFilters.add("estimate-none");
                } else {
                  newSelectedFilters.delete("estimate-none");
                }
                handleUpdateFiltersFromSelected(newSelectedFilters);
              }}
            >
              <div className="flex items-center gap-2">
                <TriangleDashed className="size-4 text-muted-foreground" />
                <span>No Estimate</span>
              </div>
              {filterCounts && (
                <FilterCount
                  count={filterCounts.estimates[DueDateFilterValue.NONE] || 0}
                />
              )}
            </DropdownMenuCheckboxItem>,
            ...ESTIMATE_POINTS.map((point) => (
              <DropdownMenuCheckboxItem
                key={point}
                className="flex items-center gap-2 select-none hover:bg-secondary-n-800 data-highlighted:bg-secondary-n-800"
                checked={selectedFilters.has(`estimate-${point}`)}
                onCheckedChange={(checked) => {
                  const newSelectedFilters = new Set(selectedFilters);
                  if (checked) {
                    newSelectedFilters.add(`estimate-${point}`);
                  } else {
                    newSelectedFilters.delete(`estimate-${point}`);
                  }
                  handleUpdateFiltersFromSelected(newSelectedFilters);
                }}
              >
                <div className="flex items-center gap-2">
                  <HalfFilledTriangleIcon className="size-4 text-muted-foreground" />
                  <span>{getTShirtSize(point)}</span>
                </div>
                {filterCounts && (
                  <FilterCount
                    count={filterCounts.estimates[point.toString()] || 0}
                  />
                )}
              </DropdownMenuCheckboxItem>
            )),
          ];
        } else if (option?.id === "dueDate") {
          items = dueDateFilterOptions.map((option) => {
            return (
              <DropdownMenuCheckboxItem
                key={option.id}
                className={cn(
                  "flex items-center gap-2 select-none hover:bg-secondary-n-800 data-highlighted:bg-secondary-n-800",
                  option.className,
                )}
                checked={selectedFilters.has(option.id)}
                onCheckedChange={(checked) => {
                  const newSelectedFilters = new Set(selectedFilters);
                  if (checked) {
                    newSelectedFilters.add(option.id);
                  } else {
                    newSelectedFilters.delete(option.id);
                  }
                  handleUpdateFiltersFromSelected(newSelectedFilters);
                }}
              >
                <div
                  className="flex items-center gap-2"
                  style={{ color: option.className }}
                >
                  <option.icon className="size-4" />
                  <span>{option.label}</span>
                </div>
                {filterCounts && (
                  <FilterCount
                    count={
                      filterCounts.dueDates[option.id.replace("due-", "")] || 0
                    }
                  />
                )}
              </DropdownMenuCheckboxItem>
            );
          });
        }

        return filterMenuItems(items, searchQuery);
      };

      return (
        <>
          <DropdownMenuItem
            className="flex items-center gap-2 cursor-pointer p-0"
            onClick={(e) => {
              e.preventDefault();
              setActiveFilter(null);
              setSearchQuery(""); // Clear search when going back
              inputRef.current?.focus();
            }}
            onSelect={(e) => e.preventDefault()}
          >
            <div className="size-5 flex items-center justify-center">
              <ChevronLeft className="size-4 text-muted-foreground" />
            </div>
            <span>{option?.label}</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator className="-mx-1" />
          <div
            className="max-h-[256px] overflow-y-auto filter-menu-scroll"
            style={{
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(255, 255, 255, 0.3) transparent",
            }}
          >
            <style>{scrollbarStyles}</style>
            {getMenuItems()}
          </div>
        </>
      );
    };

    return (
      <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            className={cn(
              "flex items-center gap-1 px-1.5 ring-1 ring-inset ring-secondary-n-800 bg-background hover:bg-secondary-n-950 transition-colors relative data-[state=open]:bg-secondary-n-950",
              className,
            )}
            size="sm"
            variant="secondary"
          >
            <div className="size-5 flex items-center justify-center">
              <ListFilter className="size-4" />
            </div>
            <p className="text-sm font-normal text-tertiary-n-500">Filter</p>
            {selectedFilters.size > 0 && (
              <div className="absolute -top-1 -right-1 size-2 rounded-full bg-orange-500" />
            )}
          </Button>
        </DropdownMenuTrigger>
        {isOpen && (
          <ShortcutHandler>
            <DropdownMenuContent
              ref={menuRef}
              align="end"
              className="w-56"
              onCloseAutoFocus={(e) => {
                e.preventDefault();
              }}
              onPointerDownOutside={(e) => {
                if (!menuRef.current?.contains(e.target as Node)) {
                  setIsOpen(false);
                }
              }}
            >
              <div className="px-2 py-2 border-secondary-n-800">
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Filter"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  className="w-full bg-transparent text-sm border-0 outline-hidden focus:ring-0 p-0 placeholder:text-muted-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  onBlur={(e) => {
                    if (menuRef.current?.contains(e.relatedTarget as Node)) {
                      e.preventDefault();
                      inputRef.current?.focus();
                    }
                  }}
                  onKeyDown={handleKeyNavigation}
                  autoFocus
                />
              </div>
              <DropdownMenuSeparator />
              {renderFilterContent()}
            </DropdownMenuContent>
          </ShortcutHandler>
        )}
      </DropdownMenu>
    );
  },
);

export default FilterMenu;
